import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ApplyModal from "./ApplyModal"

// ── Data ──────────────────────────────────────────────────────────────────────

const departments = ["All", "Engineering", "Consulting", "Sales", "Design", "Operations"];

const jobs = [
  {
    id: 1,
    title: "Senior SAP S/4HANA Consultant",
    dept: "Consulting",
    location: "Remote / New York",
    type: "Full-time",
    level: "Senior",
    salary: "$120k – $160k",
    tags: ["SAP", "S/4HANA", "ABAP", "Finance"],
    desc: "Lead end-to-end SAP S/4HANA implementations for enterprise clients. You'll own the solution design, stakeholder engagement, and delivery across finance and procurement modules.",
    requirements: [
      "5+ years SAP ECC or S/4HANA implementation experience",
      "Deep expertise in FI/CO or MM/SD modules",
      "SAP Certified Application Associate preferred",
      "Excellent client-facing communication skills",
      "Experience with SAP Activate methodology",
    ],
    posted: "2 days ago",
  },
  {
    id: 2,
    title: "Salesforce Solution Architect",
    dept: "Engineering",
    location: "Remote / London",
    type: "Full-time",
    level: "Senior",
    salary: "$130k – $170k",
    tags: ["Salesforce", "Apex", "LWC", "Architecture"],
    desc: "Design and deliver complex Salesforce solutions across Sales Cloud, Service Cloud and Marketing Cloud. You'll guide clients from discovery to go-live, owning technical architecture and team mentorship.",
    requirements: [
      "7+ years Salesforce implementation experience",
      "Salesforce Certified Technical Architect (CTA) preferred",
      "Strong Apex, LWC, and integration skills",
      "Experience leading distributed engineering teams",
      "Excellent written and verbal communication",
    ],
    posted: "3 days ago",
  },
  {
    id: 3,
    title: "Cloud Migration Engineer (AWS/Azure)",
    dept: "Engineering",
    location: "Remote / Singapore",
    type: "Full-time",
    level: "Mid-Senior",
    salary: "$100k – $140k",
    tags: ["AWS", "Azure", "Terraform", "Kubernetes"],
    desc: "Execute large-scale cloud migration projects for enterprise clients. You'll design landing zones, automate infrastructure, containerize applications, and ensure security and compliance throughout.",
    requirements: [
      "4+ years cloud engineering experience (AWS or Azure)",
      "AWS Solutions Architect or Azure Solutions Expert certification",
      "Proficiency in Terraform, Ansible, and CI/CD pipelines",
      "Experience with Kubernetes and containerization",
      "Understanding of cloud security best practices",
    ],
    posted: "5 days ago",
  },
  {
    id: 4,
    title: "AI/ML Engineer",
    dept: "Engineering",
    location: "Remote / Berlin",
    type: "Full-time",
    level: "Mid",
    salary: "$110k – $150k",
    tags: ["Python", "PyTorch", "LLM", "MLOps"],
    desc: "Build and deploy production-grade machine learning models and generative AI solutions. You'll work across the full ML lifecycle — from data engineering and model development to MLOps and monitoring.",
    requirements: [
      "3+ years hands-on ML engineering experience",
      "Strong Python, PyTorch or TensorFlow skills",
      "Experience with LLMs, RAG pipelines, and prompt engineering",
      "Familiarity with MLOps tools (MLflow, SageMaker, Vertex AI)",
      "Data engineering fundamentals (Spark, dbt, SQL)",
    ],
    posted: "1 week ago",
  },
  {
    id: 5,
    title: "Supply Chain Consultant",
    dept: "Consulting",
    location: "Chicago / Hybrid",
    type: "Full-time",
    level: "Mid",
    salary: "$90k – $120k",
    tags: ["Supply Chain", "ERP", "Logistics", "Analytics"],
    desc: "Advise clients on supply chain strategy, process redesign, and technology selection. You'll lead workshops, analyze supply chain data, and implement solutions using leading SCM platforms.",
    requirements: [
      "3+ years supply chain consulting or industry experience",
      "Knowledge of SCM platforms (blue Yonder, Kinaxis, SAP SCM)",
      "Strong analytical and data visualization skills",
      "Experience with process mapping and change management",
      "Willingness to travel up to 40%",
    ],
    posted: "1 week ago",
  },
  {
    id: 6,
    title: "UX/UI Designer",
    dept: "Design",
    location: "Remote",
    type: "Full-time",
    level: "Mid",
    salary: "$85k – $115k",
    tags: ["Figma", "UX Research", "Design Systems", "Prototyping"],
    desc: "Create intuitive, visually compelling digital experiences for enterprise software products. You'll own the end-to-end design process from user research and wireframing to high-fidelity prototypes and design systems.",
    requirements: [
      "3+ years UX/UI design experience",
      "Expert-level Figma proficiency",
      "Experience designing complex B2B/enterprise applications",
      "Strong user research and usability testing skills",
      "Portfolio demonstrating measurable design impact",
    ],
    posted: "2 weeks ago",
  },
  {
    id: 7,
    title: "Business Development Manager",
    dept: "Sales",
    location: "New York / Hybrid",
    type: "Full-time",
    level: "Senior",
    salary: "$100k – $130k + Commission",
    tags: ["B2B Sales", "Enterprise", "CRM", "Pipeline"],
    desc: "Drive new business growth by identifying, qualifying, and closing enterprise technology deals. You'll build relationships with C-suite stakeholders, understand their digital transformation needs, and position AgileTech's services.",
    requirements: [
      "5+ years enterprise B2B technology sales",
      "Track record of closing $500k+ annual contract value deals",
      "Experience selling SAP, Salesforce, or cloud services a plus",
      "Strong executive communication and negotiation skills",
      "CRM proficiency (ideally Salesforce)",
    ],
    posted: "2 weeks ago",
  },
  {
    id: 8,
    title: "Project Manager – Digital Transformation",
    dept: "Operations",
    location: "Remote / Dubai",
    type: "Full-time",
    level: "Senior",
    salary: "$95k – $125k",
    tags: ["PMP", "Agile", "Stakeholder Mgmt", "Risk"],
    desc: "Lead complex digital transformation programs across technology, people and process. You'll manage multi-workstream projects, drive stakeholder alignment, and ensure delivery quality, timeline, and budget.",
    requirements: [
      "PMP or Prince2 certification required",
      "5+ years managing enterprise technology projects",
      "Agile and Waterfall methodology proficiency",
      "Strong risk management and issue escalation skills",
      "Experience in SAP, Salesforce or cloud projects preferred",
    ],
    posted: "3 weeks ago",
  },
];

const benefits = [
  { icon: "🌍", title: "Remote-First Culture", desc: "Work from anywhere in the world. We have team members across 18 countries and counting." },
  { icon: "📈", title: "Career Growth", desc: "Structured career ladders, mentorship programs, and a dedicated L&D budget of $3,000/year per person." },
  { icon: "🏥", title: "Health & Wellness", desc: "Comprehensive medical, dental and vision coverage for you and your family. Plus a wellness stipend." },
  { icon: "💰", title: "Competitive Pay", desc: "Market-leading salaries benchmarked quarterly, performance bonuses, and equity participation for senior roles." },
  { icon: "🎓", title: "Certifications Paid", desc: "We pay for all professional certifications — SAP, Salesforce, AWS, Azure, GCP, and more." },
  { icon: "🏖️", title: "Generous PTO", desc: "25 days annual leave, 10 public holidays, and a mandatory 1-week recharge break each year." },
  { icon: "👶", title: "Parental Leave", desc: "16 weeks fully paid parental leave for all parents — primary and secondary caregivers." },
  { icon: "🖥️", title: "Home Office Setup", desc: "$1,500 home office stipend on joining + $500 annual refresh budget for equipment." },
];

const values = [
  { icon: "🔥", title: "Deliver with Excellence", desc: "We set high standards and hold ourselves accountable to outcomes, not just effort." },
  { icon: "🤝", title: "Client Obsession", desc: "Every decision starts with the question: 'Does this create value for our clients?'" },
  { icon: "🌱", title: "Grow Continuously", desc: "We invest in learning as a team — curiosity is a core competency here." },
  { icon: "💡", title: "Think Boldly", desc: "We encourage unconventional thinking and celebrate experiments, including the ones that don't work." },
];

// ── Icons ─────────────────────────────────────────────────────────────────────

const LocationIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" className="w-4 h-4 text-orange-500 shrink-0 mt-1">
    <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.6" />
    <path d="M6.5 10.5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);


// ── Job Card ──────────────────────────────────────────────────────────────────
function JobCard({ job, onApply, onExpand, isExpanded }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16, scale: 0.97 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className={`bg-white rounded-2xl border-2 transition-all duration-200 overflow-hidden
        ${isExpanded ? "border-orange-400 shadow-xl shadow-orange-50" : "border-gray-100 shadow-sm hover:border-orange-200 hover:shadow-md"}`}
    >
      {/* Card header */}
      <div
        className="p-6 cursor-pointer"
        onClick={() => onExpand(isExpanded ? null : job.id)}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="px-3 py-1 rounded-full bg-orange-50 text-orange-600 text-xs font-bold">{job.dept}</span>
              <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">{job.level}</span>
              <span className="text-gray-400 text-xs flex items-center gap-1 ml-auto"><ClockIcon />{job.posted}</span>
            </div>
            <h3 className="font-extrabold text-gray-900 text-[1.05rem] leading-snug mb-2">{job.title}</h3>
            <div className="flex items-center gap-4 text-gray-500 text-xs flex-wrap">
              <span className="flex items-center gap-1"><LocationIcon />{job.location}</span>
              <span>{job.type}</span>
              <span className="text-green-600 font-bold">{job.salary}</span>
            </div>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.25 }}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0 text-gray-500"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </motion.div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {job.tags.map((tag) => (
            <span key={tag} className="px-2.5 py-1 bg-gray-900 text-white text-[0.7rem] font-semibold rounded-full">{tag}</span>
          ))}
        </div>
      </div>

      {/* Expandable details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 border-t border-gray-100 pt-5">
              <p className="text-gray-600 text-[0.91rem] leading-relaxed mb-5">{job.desc}</p>

              <p className="font-bold text-gray-900 text-[0.93rem] mb-3">Requirements</p>
              <ul className="space-y-2 mb-6">
                {job.requirements.map((req, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <CheckIcon />
                    <span className="text-gray-600 text-[0.88rem] leading-relaxed">{req}</span>
                  </li>
                ))}
              </ul>

              <motion.button
                onClick={() => onApply(job)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="w-full sm:w-auto px-8 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full transition-colors text-[0.93rem]"
              >
                Apply for this Role →
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Main Career Page ──────────────────────────────────────────────────────────
export default function CareerPage() {
  const [activeDept, setActiveDept] = useState("All");
  const [expandedJob, setExpandedJob] = useState(null);
  const [applyJob, setApplyJob] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const filtered = jobs.filter((j) => {
    const matchesDept = activeDept === "All" || j.dept === activeDept;
    const matchesSearch =
      j.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      j.tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesDept && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-white">

      {/* ── Hero ── */}

      {/* ── Culture / Values ── */}
      <section id="culture" className="py-8 lg:py-12 bg-[#f5f7fa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-block px-5 py-2 font-jakarta rounded-full border border-gray-300 bg-white text-gray-500 text-xs font-bold tracking-widest uppercase mb-4"
            >
              Our Culture
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl sm:text-4xl font-jakarta font-bold text-gray-900"
            >
              What Drives Us Every Day
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                whileHover={{ y: -6 }}
                className="bg-white rounded-2xl p-7 border border-gray-100 shadow-sm"
              >
                <div className="text-4xl mb-4">{v.icon}</div>
                <h3 className="font-extrabold text-gray-900 text-[1rem] mb-2">{v.title}</h3>
                <p className="text-gray-500 text-[0.88rem] leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Benefits ── */}
      <section className="py-8 lg:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-block px-5 py-2 font-jakarta rounded-full border border-gray-300 bg-gray-50 text-gray-500 text-xs font-bold tracking-widest uppercase mb-4"
            >
              Perks & Benefits
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl sm:text-4xl font-jakarta font-bold text-gray-900"
            >
              We Take Care of Our People
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07, duration: 0.45 }}
                whileHover={{ y: -4, boxShadow: "0 12px 32px rgba(59,130,246,0.10)" }}
                className="bg-gray-50 border border-gray-100 rounded-2xl p-6 group cursor-default"
              >
                <div className="text-3xl mb-3">{b.icon}</div>
                <h3 className="font-bold text-gray-900 text-[0.97rem] mb-1.5">{b.title}</h3>
                <p className="text-gray-500 text-[0.85rem] leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Open Roles ── */}
      <section id="open-roles" className="py-8 lg:py-12 bg-[#f5f7fa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-block px-5 font-jakarta py-2 rounded-full border border-gray-300 bg-white text-gray-500 text-xs font-bold tracking-widest uppercase mb-4"
            >
              Open Positions
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl sm:text-4xl font-extrabold text-gray-900"
            >
              Find Your Perfect Role
            </motion.h2>
          </div>

          {/* Search + Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="flex-1 relative">
              <svg viewBox="0 0 20 20" fill="none" className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2">
                <path d="M13 13l4 4M8 15A7 7 0 108 1a7 7 0 000 14z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                placeholder="Search by role or skill..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-[0.92rem] focus:outline-none focus:border-orange-400 transition-colors bg-white"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {departments.map((dept) => (
                <button
                  key={dept}
                  onClick={() => setActiveDept(dept)}
                  className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all duration-200
                    ${activeDept === dept ? "bg-orange-500 text-white shadow-md" : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300"}`}
                >
                  {dept}
                </button>
              ))}
            </div>
          </div>

          {/* Results count */}
          <p className="text-sm text-gray-500 mb-5">
            Showing <span className="font-bold text-gray-900">{filtered.length}</span> open {filtered.length === 1 ? "position" : "positions"}
            {activeDept !== "All" && <> in <span className="font-bold text-orange-500">{activeDept}</span></>}
          </p>

          {/* Job list */}
          <div className="space-y-4">
            <AnimatePresence mode="popLayout">
              {filtered.length > 0 ? (
                filtered.map((job) => (
                  <JobCard
                    key={job.id}
                    job={job}
                    onApply={setApplyJob}
                    onExpand={setExpandedJob}
                    isExpanded={expandedJob === job.id}
                  />
                ))
              ) : (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-20 bg-white rounded-2xl border border-gray-100"
                >
                  <div className="text-5xl mb-4">🔍</div>
                  <p className="text-gray-900 font-bold text-lg mb-2">No roles found</p>
                  <p className="text-gray-500 text-sm">Try a different search or department filter.</p>
                  <button onClick={() => { setActiveDept("All"); setSearchQuery(""); }} className="mt-5 px-6 py-2.5 bg-orange-500 text-white font-bold rounded-full text-sm">
                    Clear Filters
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#0d1b2e] to-[#1a3a5c] rounded-3xl px-8 sm:px-14 py-14 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-10"
              style={{ backgroundImage: "radial-gradient(circle, #60A5FA 1px, transparent 1px)", backgroundSize: "28px 28px" }}
            />
            <div className="relative z-10">
              <p className="text-orange-400 text-xs font-bold tracking-widest uppercase mb-3">Don't See a Fit?</p>
              <h2 className="text-white font-extrabold text-3xl sm:text-4xl mb-4">
                Send Us Your CV Anyway
              </h2>
              <p className="text-gray-400 max-w-lg mx-auto text-[0.95rem] leading-relaxed mb-8">
                We're always looking for exceptional talent. Drop us your resume and we'll reach out when the right opportunity comes along.
              </p>
              <motion.button
              onClick={()=> setApplyJob("nothing")}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                className="px-10 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full text-[0.95rem] transition-colors"
              >
                Send Your CV →
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Apply Modal */}
      <AnimatePresence>
        {applyJob && <ApplyModal job={applyJob} onClose={() => setApplyJob(null)} />}
      </AnimatePresence>
    </div>
  );
}